/**
 * Static copy for the Cup O' Joe podcast landing page: hero text, image paths, the show's links on
 * Apple Podcasts, Spotify, Amazon Music and YouTube, and button labels, in English, French and
 * Spanish (real translations; the URLs and images are the same in all three).
 *
 * Read by `./Podcasts.tsx` (`podcastStaticContent`), which passes one language's entry to
 * `./Podcast.helpers.tsx` → `PodcastHeroSection`. The episode pages don't use it: each episode's
 * platform links come from Contentful (`./PodcastDetail.tsx`).
 */
import { MetaContent } from "@components/Shared/model/MetaHead";
import { LanguageContent } from "@ryerson/frontend.common";

/** One language's landing-page copy. Image paths are site-relative, under `public/images/`. */
export type PodcastStaticContent = {
	title: string;
	blurb: string;
	/** The hero's background photo. */
	backgroundImage: string;
	/** The Cup O' Joe logo shown in the hero. */
	imageUrl: string;
	followUs: string;
	/** The show's page on each platform (not an episode link). */
	apple: string;
	spotify: string;
	amazon: string;
	youtube: string;
	episodes: string;
	viewEpisode: string;
	loadMore: string;
	/** Label of the button that opens `./Podcast.helpers.tsx` → `cloudRegistrationUrl`. */
	registerForLiveShow: string;
};

/**
 * The landing page's SEO head. Nothing imports it: the page
 * (`src/pages/metal-resources/metal-market-intelligence/podcast/index.tsx`) declares an identical
 * `MetaObject` of its own. `fr` and `es` repeat the English.
 */
export const MetaObject: LanguageContent<MetaContent> = {
	en: {
		title: "Cup O' Joe - A Metal Podcast - Ryerson",
		description: `Welcome to Cup o’ Joe, your go-to podcast for a fun and informative look at the dynamic world of metals!`,
		url: `/metal-resources/metal-market-intelligence/podcast`,
		company: false,
	},
	fr: {
		title: "Cup O' Joe - A Metal Podcast - Ryerson",
		description: `Welcome to Cup o’ Joe, your go-to podcast for a fun and informative look at the dynamic world of metals!`,
		url: `/metal-resources/metal-market-intelligence/podcast`,
		company: false,
	},
	es: {
		title: "Cup O' Joe - A Metal Podcast - Ryerson",
		description: `Welcome to Cup o’ Joe, your go-to podcast for a fun and informative look at the dynamic world of metals!`,
		url: `/metal-resources/metal-market-intelligence/podcast`,
		company: false,
	},
};

/** The landing page's copy per language. */
export const podcastStaticContent: LanguageContent<PodcastStaticContent> = {
	en: {
		title: "Cup O' Joe: A Metal Podcast",
		blurb: "Welcome to Cup o’ Joe, your go-to podcast for a fun and informative look at the dynamic world of metals! In each episode, we bring the latest factors influencing the price and availability of metals, led by the Director of Risk Management and Commodities Hedging at Ryerson, Nick Webb, and hosted by Mike Carrozzo.",
		backgroundImage: "/images/metal-resources/metalMarketIntelligenceUpdate2.jpg",
		imageUrl: "/images/metal-resources/metal-market-intelligence/CupOJoe.png",
		followUs: "Follow us on:",
		apple: "https://podcasts.apple.com/ca/podcast/cup-o-joe-a-metals-podcast/id1735785756",
		spotify: "https://open.spotify.com/show/5pWqd3YrKIMQ4VV8MmxSbY?si=184d7ca1614a46d7",
		amazon: "https://amzn.in/d/3zmc6M4",
		youtube:
			"https://youtube.com/playlist?list=PLmlIF_4-3cPuaBvHASGlmZZtlAKuB3qgE&si=AIhUIIMhNOS6-8cE",
		episodes: "Episodes",
		loadMore: "Load More Episodes",
		viewEpisode: "View Episode",
		registerForLiveShow: "Register for the Live Show!",
	},
	fr: {
		title: "Cup O' Joe: Un balado sur les métaux",
		blurb: "Bienvenue à Cup o’ Joe, votre balado de prédilection pour une vue amusante et informative du monde dynamique des métaux! Dans chaque épisode, nous présentons les derniers facteurs influençant le prix et la disponibilité des métaux, dirigés par le directeur de la gestion des risques et de la couverture des matières premières chez Ryerson, Nick Webb, et animé par Mike Carrozzo.",
		backgroundImage: "/images/metal-resources/metalMarketIntelligenceUpdate2.jpg",
		imageUrl: "/images/metal-resources/metal-market-intelligence/CupOJoe.png",
		followUs: "Suivez-nous sur:",
		apple: "https://podcasts.apple.com/ca/podcast/cup-o-joe-a-metals-podcast/id1735785756",
		spotify: "https://open.spotify.com/show/5pWqd3YrKIMQ4VV8MmxSbY?si=184d7ca1614a46d7",
		amazon: "https://amzn.in/d/3zmc6M4",
		youtube:
			"https://youtube.com/playlist?list=PLmlIF_4-3cPuaBvHASGlmZZtlAKuB3qgE&si=AIhUIIMhNOS6-8cE",
		episodes: "Épisodes",
		loadMore: "Charger plus d'épisodes",
		viewEpisode: "Voir l'épisode",
		registerForLiveShow: "Inscrivez-vous à l’évènement en direct!",
	},
	es: {
		title: "Cup O' Joe: Un podcast de metales",
		blurb: "¡Bienvenido a Cup o’ Joe, tu podcast de referencia para una mirada divertida e informativa al dinámico mundo de los metales! En cada episodio, presentamos los últimos factores que influyen en el precio y la disponibilidad de los metales, dirigidos por el Director de Gestión de Riesgos y Cobertura de Materias Primas en Ryerson, Nick Webb, y presentado por Mike Carrozzo.",
		backgroundImage: "/images/metal-resources/metalMarketIntelligenceUpdate2.jpg",
		imageUrl: "/images/metal-resources/metal-market-intelligence/CupOJoe.png",
		followUs: "Síguenos en:",
		apple: "https://podcasts.apple.com/ca/podcast/cup-o-joe-a-metals-podcast/id1735785756",
		spotify: "https://open.spotify.com/show/5pWqd3YrKIMQ4VV8MmxSbY?si=184d7ca1614a46d7",
		amazon: "https://amzn.in/d/3zmc6M4",
		youtube:
			"https://youtube.com/playlist?list=PLmlIF_4-3cPuaBvHASGlmZZtlAKuB3qgE&si=AIhUIIMhNOS6-8cE",
		episodes: "Episodios",
		loadMore: "Cargar más episodios",
		viewEpisode: "Ver episodio",
		registerForLiveShow: "Regístrese para el evento en vivo",
	},
};
